import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mapResponse } from '@ngrx/operators';
import { concatMap, delay } from 'rxjs';
import { SnmpVaultService } from '../../../api/services';
import { getErrorMessage, ZetraError } from '../../shared/errors/error-messages.helper';
import { LoadingState } from '../models/loader.model';
import { snmpVaultActions } from './snmp-vault.action';

export const usersLoadEffect = createEffect(
  (actions$ = inject(Actions), usersService = inject(SnmpVaultService)) => {
    return actions$.pipe(
      ofType(snmpVaultActions.load),
      concatMap((props) =>
        usersService.snmpVaultControllerFindAll(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => snmpVaultActions.done({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              snmpVaultActions.done({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const snmpVaultLoadOneEffect = createEffect(
  (actions$ = inject(Actions), service = inject(SnmpVaultService)) => {
    return actions$.pipe(
      ofType(snmpVaultActions.loadOne),
      concatMap((props) =>
        service.snmpVaultControllerGetOne(props).pipe(
          mapResponse({
            next: (r) => snmpVaultActions.loadOneDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              snmpVaultActions.loadOneDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const snmpVaultCreateEffect = createEffect(
  (actions$ = inject(Actions), service = inject(SnmpVaultService)) => {
    return actions$.pipe(
      ofType(snmpVaultActions.create),
      concatMap((props) =>
        service.snmpVaultControllerCreate(props).pipe(
          mapResponse({
            next: (r) => snmpVaultActions.createDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              snmpVaultActions.createDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const snmpVaultEditEffect = createEffect(
  (actions$ = inject(Actions), service = inject(SnmpVaultService)) => {
    return actions$.pipe(
      ofType(snmpVaultActions.edit),
      concatMap((props) =>
        service.snmpVaultControllerUpdate(props).pipe(
          mapResponse({
            next: (r) => snmpVaultActions.editDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              snmpVaultActions.editDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const snmpVaultDeleteEffect = createEffect(
  (actions$ = inject(Actions), service = inject(SnmpVaultService)) => {
    return actions$.pipe(
      ofType(snmpVaultActions.delete),
      concatMap((props) =>
        service.snmpVaultControllerDelete(props).pipe(
          mapResponse({
            next: (r) => snmpVaultActions.deleteDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              snmpVaultActions.deleteDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const snmpVaultAddPrintersEffect = createEffect(
  (actions$ = inject(Actions), service = inject(SnmpVaultService)) => {
    return actions$.pipe(
      ofType(snmpVaultActions.connectManyPrinters),
      concatMap((props) =>
        service.snmpVaultControllerAddPrinters(props).pipe(
          mapResponse({
            next: (r) =>
              snmpVaultActions.connectManyPrintersDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              snmpVaultActions.connectManyPrintersDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
